import {
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CodeBlock,
  Divider,
  Badge,
  Banner,
  TableOfContents,
  Animate,
  useIsDesktop
} from 'wss3-forge'
import { Box20Regular, Shield20Regular, Key20Regular, ArrowSync20Regular, SignOut20Regular, Settings20Regular, Organization20Regular, People20Regular, Checkmark20Regular, Link20Regular, Search20Regular } from '@fluentui/react-icons'

export function DocsSdk() {
  const isDesktop = useIsDesktop()

  const tocItems = [
    { id: 'installation', title: 'Installation' },
    { id: 'configuration', title: 'Configuration' },
    { id: 'login', title: 'Login' },
    { id: 'callback', title: 'Callback' },
    { id: 'user', title: 'Utilisateur' },
    { id: 'refresh', title: 'Refresh Tokens' },
    { id: 'logout', title: 'Deconnexion' },
    { id: 'organizations', title: 'Organizations' },
    { id: 'members', title: 'Membres' },
    { id: 'appaccess', title: 'App Access' },
    { id: 'identities', title: 'Identites liees' },
    { id: 'reverselookup', title: 'Reverse Lookup' },
    { id: 'authcontext', title: 'AuthContext React' }
  ]

  return (
    <div className="page-content">
      <HStack gap="xl" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <VStack gap="xl">
            <Animate type="fadeIn">
              <VStack gap="sm" style={{ alignItems: 'flex-start' }}>
                <HStack gap="sm">
                  <Badge variant="primary">SDK</Badge>
                  <Badge variant="default">v2.3.0</Badge>
                </HStack>
                <Heading level={1}>SDK Webba ID</Heading>
                <Text color="muted" size="lg">
                  Integrez l'authentification Webba ID avec OAuth 2.0 + PKCE en quelques lignes de code.
                </Text>
              </VStack>
            </Animate>

            <div id="installation" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={100}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Box20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Installation</Heading>
                    </HStack>

                    <Text color="muted">
                      Copiez le fichier SDK dans votre projet ou installez via npm :
                    </Text>

                    <CodeBlock
                      code={`npm install webba-id-sdk

# Ou copiez src/lib/webba-id.ts dans votre projet`}
                      language="bash"
                    />
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="configuration" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={150}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Settings20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Configuration</Heading>
                    </HStack>

                    <Text color="muted">
                      Configurez le SDK une fois au demarrage de votre application (main.tsx).
                    </Text>

                    <Text weight="medium">1. Variables d'environnement (.env) :</Text>
                    <CodeBlock
                      code={`VITE_WEBBA_API_KEY=your_webba_id_supabase_anon_key
VITE_WEBBA_CLIENT_ID=wba_xxx
VITE_WEBBA_CLIENT_SECRET=wbs_xxx
VITE_WEBBA_REDIRECT_URI=http://localhost:5173/auth/callback`}
                      language="bash"
                    />

                    <Text weight="medium">2. Configuration dans main.tsx :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

// Configurer le SDK (une seule fois)
WebbaID.configure({
  apiKey: import.meta.env.VITE_WEBBA_API_KEY,
  clientId: import.meta.env.VITE_WEBBA_CLIENT_ID,
  clientSecret: import.meta.env.VITE_WEBBA_CLIENT_SECRET,
  redirectUri: import.meta.env.VITE_WEBBA_REDIRECT_URI
})

// Puis rendre l'app
ReactDOM.createRoot(document.getElementById('root')!).render(
  <App />
)`}
                      language="typescript"
                    />

                    <Banner variant="info" title="Credentials">
                      Obtenez vos credentials sur https://account.webba-creative.com/platform/applications
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="login" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={200}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Key20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Login</Heading>
                    </HStack>
                    <Text color="muted">
                      Redirigez l'utilisateur vers Webba ID pour l'authentification.
                      Le SDK gere automatiquement PKCE et le state CSRF.
                    </Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

async function handleLogin() {
  // Redirige vers account.webba-creative.com
  await WebbaID.login()
}

// Dans votre composant
<button onClick={handleLogin}>
  Se connecter avec Webba ID
</button>`}
                      language="typescript"
                    />
                    <Banner variant="info" title="PKCE automatique">
                      Le SDK genere automatiquement le code_verifier et code_challenge,
                      stockes dans un cookie + localStorage pour la securite cross-origin.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="callback" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={250}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Shield20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Page Callback</Heading>
                    </HStack>
                    <Text color="muted">
                      Creez une page /auth/callback pour gerer le retour apres authentification.
                    </Text>
                    <CodeBlock
                      code={`// src/pages/AuthCallback.tsx
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WebbaID } from '@/lib/webba-id'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const processingRef = useRef(false)

  useEffect(() => {
    const processCallback = async () => {
      // Protection contre double execution (React StrictMode)
      if (processingRef.current) return
      processingRef.current = true

      // Verifier qu'on est sur un callback valide
      if (!WebbaID.isCallback()) {
        setError('Page de callback invalide')
        return
      }

      try {
        // Echanger le code contre des tokens
        const tokens = await WebbaID.handleCallback()

        // Recuperer les infos utilisateur
        const user = await WebbaID.getUser(tokens.accessToken)

        // Sauvegarder en localStorage
        WebbaID.saveTokens(tokens)
        WebbaID.saveUser(user)

        // Rediriger vers le dashboard
        navigate('/dashboard')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur inconnue')
        processingRef.current = false
      }
    }

    processCallback()
  }, [navigate])

  if (error) {
    return (
      <div>
        <h2>Erreur: {error}</h2>
        <a href="/">Retour a l'accueil</a>
      </div>
    )
  }

  return <div>Connexion en cours...</div>
}`}
                      language="typescript"
                    />
                    <Banner variant="warning" title="useRef important">
                      Le useRef evite la double execution du callback en React StrictMode,
                      ce qui causerait une erreur "code already used".
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="user" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={300}>
                <Card padding="lg">
                  <VStack gap="md">
                    <Heading level={3}>Utiliser les tokens</Heading>
                    <Text color="muted">
                      Recuperez l'utilisateur connecte et verifiez l'expiration des tokens.
                    </Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

// Recuperer l'utilisateur stocke
const user = WebbaID.getStoredUser()
const tokens = WebbaID.getStoredTokens()

// Verifier si connecte
if (user && tokens && !WebbaID.isTokenExpired(tokens)) {
  console.log('Connecte:', user.email)
}

// Types disponibles
interface WebbaUser {
  id: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  avatar?: string
}

interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number  // timestamp ms
  userId: string
}`}
                      language="typescript"
                    />
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="refresh" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={350}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <ArrowSync20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Refresh Tokens</Heading>
                    </HStack>
                    <Text color="muted">
                      Les access_tokens expirent apres 1 heure. Rafraichissez-les automatiquement.
                    </Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

async function ensureValidToken() {
  const tokens = WebbaID.getStoredTokens()

  if (!tokens) {
    // Pas connecte
    return null
  }

  // Token encore valide
  if (!WebbaID.isTokenExpired(tokens)) {
    return tokens
  }

  // Token expire, rafraichir
  try {
    const newTokens = await WebbaID.refreshTokens(tokens.refreshToken)
    WebbaID.saveTokens(newTokens)
    return newTokens
  } catch {
    // Refresh echoue, deconnecter
    WebbaID.clearTokens()
    return null
  }
}`}
                      language="typescript"
                    />
                    <Banner variant="warning" title="Rotation des tokens">
                      Chaque refresh genere un nouveau refresh_token. L'ancien devient invalide.
                      Sauvegardez toujours le nouveau refresh_token.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="logout" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={400}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <SignOut20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Deconnexion</Heading>
                    </HStack>
                    <Text color="muted">
                      Revoquez les tokens et nettoyez le stockage local.
                    </Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

async function handleLogout() {
  const tokens = WebbaID.getStoredTokens()

  // Revoquer le token cote serveur + nettoyer localStorage
  await WebbaID.logout(tokens?.accessToken)

  // Rediriger vers l'accueil
  window.location.href = '/'
}`}
                      language="typescript"
                    />
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="organizations" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={450}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Organization20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Organizations</Heading>
                    </HStack>
                    <Text color="muted">
                      Gerez les organisations de l'utilisateur : liste, creation, modification, suppression.
                    </Text>

                    <Divider />

                    <Text weight="medium">Types disponibles :</Text>
                    <CodeBlock
                      code={`type OrgRole = 'owner' | 'admin' | 'sso_admin' | 'viewer' | 'member'

interface Organization {
  id: string
  name: string
  slug: string
  logo_url: string | null
  is_active: boolean
  created_at: string
  role: OrgRole       // Role de l'utilisateur
  members_count?: number
  domains_count?: number
}`}
                      language="typescript"
                    />

                    <Text weight="medium">Lister les organisations :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

const tokens = WebbaID.getStoredTokens()

// Recuperer toutes les organisations de l'utilisateur
const orgs = await WebbaID.getOrganizations(tokens.accessToken)

console.log(orgs)
// [{ id: '...', name: 'My Company', role: 'owner', ... }]`}
                      language="typescript"
                    />

                    <Text weight="medium">Creer une organisation :</Text>
                    <CodeBlock
                      code={`// L'utilisateur devient automatiquement owner
const newOrg = await WebbaID.createOrganization(tokens.accessToken, {
  name: 'Mon Entreprise',
  slug: 'mon-entreprise',  // optionnel, genere auto si absent
  logo_url: 'https://...'  // optionnel
})

console.log(newOrg.role) // 'owner'`}
                      language="typescript"
                    />

                    <Text weight="medium">Details d'une organisation :</Text>
                    <CodeBlock
                      code={`const org = await WebbaID.getOrganization(tokens.accessToken, orgId)

console.log(org.domains)        // Liste des domaines
console.log(org.members_count)  // Nombre de membres`}
                      language="typescript"
                    />

                    <Text weight="medium">Modifier une organisation :</Text>
                    <CodeBlock
                      code={`// Requiert role 'owner' ou 'admin'
const updated = await WebbaID.updateOrganization(tokens.accessToken, orgId, {
  name: 'Nouveau Nom',
  logo_url: 'https://nouveau-logo.png'
})`}
                      language="typescript"
                    />

                    <Text weight="medium">Supprimer une organisation :</Text>
                    <CodeBlock
                      code={`// Requiert role 'owner' uniquement
await WebbaID.deleteOrganization(tokens.accessToken, orgId)`}
                      language="typescript"
                    />

                    <Banner variant="info" title="Permissions">
                      owner = tout · admin = modifier/membres · sso_admin = SSO · viewer = lecture · member = base
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="members" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={500}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <People20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Membres</Heading>
                    </HStack>
                    <Text color="muted">
                      Gerez les membres d'une organisation : liste, invitation, modification de role, suppression.
                    </Text>

                    <Divider />

                    <Text weight="medium">Type OrganizationMember :</Text>
                    <CodeBlock
                      code={`interface OrganizationMember {
  id: string
  organization_id: string
  user_id: string | null      // null si invitation en attente
  role: OrgRole
  created_via: 'sso' | 'invite' | 'manual'
  email?: string
  profile?: {
    first_name?: string
    last_name?: string
    display_name?: string
    avatar_url?: string
  }
  invite_email?: string       // Email d'invitation
  invite_accepted_at?: string // Date d'acceptation
}`}
                      language="typescript"
                    />

                    <Text weight="medium">Lister les membres :</Text>
                    <CodeBlock
                      code={`const members = await WebbaID.getOrganizationMembers(
  tokens.accessToken,
  orgId
)

members.forEach(m => {
  console.log(m.email, m.role, m.profile?.display_name)
})`}
                      language="typescript"
                    />

                    <Text weight="medium">Inviter un membre :</Text>
                    <CodeBlock
                      code={`// Requiert role 'owner' ou 'admin'
const invited = await WebbaID.inviteMember(tokens.accessToken, orgId, {
  email: 'nouveau@example.com',
  role: 'member'  // 'admin' | 'sso_admin' | 'viewer' | 'member'
})

// Le membre recevra un email d'invitation`}
                      language="typescript"
                    />

                    <Text weight="medium">Modifier le role :</Text>
                    <CodeBlock
                      code={`// Requiert role 'owner'
await WebbaID.updateMemberRole(
  tokens.accessToken,
  orgId,
  memberId,
  'admin'  // nouveau role
)`}
                      language="typescript"
                    />

                    <Text weight="medium">Retirer un membre :</Text>
                    <CodeBlock
                      code={`// Requiert role 'owner' ou 'admin'
// (ou l'utilisateur peut se retirer lui-meme)
await WebbaID.removeMember(tokens.accessToken, orgId, memberId)`}
                      language="typescript"
                    />

                    <Banner variant="warning" title="Restrictions">
                      Le owner ne peut pas etre retire. Pour transferer la propriete, promouvoir d'abord un admin.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="appaccess" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={525}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Checkmark20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>App Access</Heading>
                    </HStack>
                    <Text color="muted">
                      Verifiez si un utilisateur a acces a votre application (abonnement ou acces organisation).
                    </Text>

                    <Divider />

                    <Text weight="medium">Verification complete :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

const tokens = WebbaID.getStoredTokens()

// Verification complete avec details
const access = await WebbaID.checkAppAccess(tokens.accessToken)

if (!access.has_access) {
  // Pas d'acces
  console.log('Acces refuse:', access.reason)
  // 'no_access' | 'subscription' | 'org_access' | 'subscription_and_org'
  window.location.href = '/upgrade'
}

// Verifier les details
if (access.subscription) {
  console.log('Plan:', access.subscription.plan)
  console.log('Features:', access.subscription.features)
}

if (access.organizations.length > 0) {
  console.log('Organisations avec acces:', access.organizations)
}`}
                      language="typescript"
                    />

                    <Text weight="medium">Verification rapide (boolean) :</Text>
                    <CodeBlock
                      code={`// Retourne simplement true/false
if (await WebbaID.hasAccess(tokens.accessToken)) {
  // L'utilisateur peut utiliser l'app
} else {
  // Pas d'acces - rediriger vers upgrade
}

// Verifier pour une organisation specifique
const orgAccess = await WebbaID.checkAppAccess(
  tokens.accessToken,
  'org-uuid'
)`}
                      language="typescript"
                    />

                    <Text weight="medium">Type AppAccessResult :</Text>
                    <CodeBlock
                      code={`interface AppAccessResult {
  has_access: boolean
  reason: 'subscription' | 'org_access' | 'subscription_and_org' | 'no_access'
  user_id: string
  app_id: string
  subscription: {
    id: string
    plan: string
    status: string
    features: Record<string, any> | null
    starts_at: string | null
    ends_at: string | null
  } | null
  organizations: Array<{
    id: string
    name: string
    slug: string
    role: string
  }>
}`}
                      language="typescript"
                    />

                    <Banner variant="info" title="Conditions d'acces">
                      L'acces est accorde si l'utilisateur a un abonnement actif pour l'app OU est membre d'une organisation ou l'app est activee.
                    </Banner>

                    <Divider />

                    <Text weight="medium">Auto-activation pour une organisation :</Text>
                    <CodeBlock
                      code={`// L'app peut s'activer elle-meme pour une organisation
const orgs = await WebbaID.getOrganizations(tokens.accessToken)

if (orgs.length > 0) {
  // Activer l'app pour l'organisation
  await WebbaID.enableAppForOrg(tokens.accessToken, orgs[0].id)
}

// Maintenant checkAppAccess retourne has_access: true
const access = await WebbaID.checkAppAccess(tokens.accessToken, orgs[0].id)
console.log(access.has_access) // true

// Pour desactiver
await WebbaID.disableAppForOrg(tokens.accessToken, orgs[0].id)`}
                      language="typescript"
                    />

                    <Banner variant="warning" title="Permissions">
                      L'utilisateur doit etre membre de l'organisation pour activer/desactiver l'app.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="identities" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={575}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Link20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Identites liees (Cfx.re)</Heading>
                    </HStack>
                    <Text color="muted">
                      Verifiez si un utilisateur a lie son compte Cfx.re (FiveM/RedM) et recuperez son identifiant.
                    </Text>

                    <Divider />

                    <Text weight="medium">Types disponibles :</Text>
                    <CodeBlock
                      code={`interface LinkedIdentity {
  provider: string              // 'cfxre', 'discord', 'google', etc.
  provider_user_id: string      // ID unique chez le provider
  provider_username: string | null
  provider_email: string | null
  provider_avatar_url: string | null
  linked_at: string
  last_used_at: string
}

interface CfxreIdentity {
  cfxre_id: string              // ID Cfx.re (ex: "123456")
  username: string | null       // Nom d'utilisateur forum
  avatar_url: string | null
  linked_at: string
}`}
                      language="typescript"
                    />

                    <Text weight="medium">Verifier si Cfx.re est lie :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from '@/lib/webba-id'

const tokens = WebbaID.getStoredTokens()

// Verification rapide
if (await WebbaID.hasCfxreLinked(tokens.accessToken)) {
  console.log('Cfx.re est lie!')
} else {
  // Rediriger vers la page de liaison
  window.location.href = 'https://account.webba-creative.com/linked-accounts'
}`}
                      language="typescript"
                    />

                    <Text weight="medium">Recuperer l'ID Cfx.re :</Text>
                    <CodeBlock
                      code={`// Juste l'ID
const cfxreId = await WebbaID.getCfxreId(tokens.accessToken)
console.log('Cfx.re ID:', cfxreId) // "123456"

// Ou les details complets
const cfxre = await WebbaID.getCfxreIdentity(tokens.accessToken)
console.log(cfxre)
// {
//   cfxre_id: '123456',
//   username: 'PlayerName',
//   avatar_url: 'https://...',
//   linked_at: '2024-01-01T00:00:00Z'
// }`}
                      language="typescript"
                    />

                    <Text weight="medium">Lister toutes les identites liees :</Text>
                    <CodeBlock
                      code={`// Tous les providers lies (pas que Cfx.re)
const identities = await WebbaID.getLinkedIdentities(tokens.accessToken)

console.log(identities)
// [
//   { provider: 'cfxre', provider_user_id: '123456', ... },
//   { provider: 'discord', provider_user_id: '789...', ... }
// ]`}
                      language="typescript"
                    />

                    <Banner variant="info" title="Cas d'usage">
                      Utilisez ces methodes pour verifier qu'un joueur a bien lie son compte FiveM/RedM avant d'acceder a certaines fonctionnalites de votre application.
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="reverselookup" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={600}>
                <Card padding="lg">
                  <VStack gap="md">
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Search20Regular style={{ color: 'var(--brand-primary)' }} />
                      <Heading level={3}>Reverse Lookup (Server-side)</Heading>
                      <Badge variant="warning">appSecret requis</Badge>
                    </HStack>
                    <Text color="muted">
                      Trouvez un utilisateur Webba ID a partir de son ID provider (ex: Discord ID). Utile pour les bots et APIs serveur.
                    </Text>

                    <Divider />

                    <Text weight="medium">Configuration serveur :</Text>
                    <CodeBlock
                      code={`import { WebbaID } from 'webba-id-sdk'

// Configuration serveur (avec appSecret)
const webbaId = new WebbaID({
  appKey: 'wba_xxx',
  appSecret: 'wbs_xxx',  // Requis pour le reverse lookup
  redirectUri: 'https://example.com/callback'
})`}
                      language="typescript"
                    />

                    <Text weight="medium">Lookup simple (un seul ID) :</Text>
                    <CodeBlock
                      code={`// Exemple: Bot Discord qui cherche un utilisateur Webba ID
const result = await webbaId.lookupUserByProvider('discord', message.author.id)

if (result.user) {
  console.log('Webba ID:', result.user.user_id)
  console.log('Email:', result.user.email)
} else {
  console.log('Cet utilisateur Discord n\\'est pas lie a Webba ID')
}

// Type retourne:
// {
//   provider_user_id: '123456789',
//   user: { user_id: 'uuid', email: 'user@example.com' } | null
// }`}
                      language="typescript"
                    />

                    <Text weight="medium">Lookup batch (plusieurs IDs) :</Text>
                    <CodeBlock
                      code={`// Verifier plusieurs utilisateurs Discord d'un coup (max 100)
const discordIds = ['123456789', '987654321', '111222333']
const batch = await webbaId.lookupUsersByProvider('discord', discordIds)

console.log(batch.mapping)
// { '123456789': 'webba-uuid-1', '987654321': 'webba-uuid-2' }
// Note: '111222333' absent = pas lie

// Verifier si un ID specifique est lie
if (batch.mapping['123456789']) {
  console.log('User lie:', batch.mapping['123456789'])
}`}
                      language="typescript"
                    />

                    <Divider />

                    <Text weight="medium">Recuperer les identites d'un user_id :</Text>
                    <CodeBlock
                      code={`// Obtenir TOUTES les identites liees d'un utilisateur Webba ID
const result = await webbaId.getUserIdentitiesByUserId('webba-user-uuid')

console.log(result.identities)
// [
//   { provider: 'discord', provider_user_id: '123456789', ... },
//   { provider: 'cfxre', provider_user_id: '987654', ... }
// ]

// Obtenir un provider ID specifique
const cfxreId = await webbaId.getProviderIdByUserId('webba-user-uuid', 'cfxre')

// Raccourcis pratiques
const cfxre = await webbaId.getCfxreIdByUserId('webba-user-uuid')
const discord = await webbaId.getDiscordIdByUserId('webba-user-uuid')`}
                      language="typescript"
                    />

                    <Banner variant="warning" title="Securite">
                      Ces methodes necessitent appSecret et sont reservees au code serveur. Ne jamais exposer appSecret cote client.
                    </Banner>

                    <Banner variant="info" title="Providers supportes">
                      discord, google, github, azure, cfxre
                    </Banner>
                  </VStack>
                </Card>
              </Animate>
            </div>

            <div id="authcontext" style={{ scrollMarginTop: '100px' }}>
              <Animate type="slideInUp" delay={650}>
                <Card padding="lg">
                  <VStack gap="md">
                    <Heading level={3}>AuthContext React</Heading>
                    <Text color="muted">
                      Exemple complet d'un contexte d'authentification pour React.
                    </Text>
                    <CodeBlock
                      code={`// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { WebbaID, AuthTokens, WebbaUser } from '@/lib/webba-id'

interface AuthContextType {
  user: WebbaUser | null
  tokens: AuthTokens | null
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  setAuth: (tokens: AuthTokens, user: WebbaUser) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<WebbaUser | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Charger depuis localStorage au demarrage
    const storedTokens = WebbaID.getStoredTokens()
    const storedUser = WebbaID.getStoredUser()

    if (storedTokens && storedUser && !WebbaID.isTokenExpired(storedTokens)) {
      setTokens(storedTokens)
      setUser(storedUser)
    }
    setIsLoading(false)
  }, [])

  const login = async () => {
    await WebbaID.login()
  }

  const logout = async () => {
    await WebbaID.logout(tokens?.accessToken)
    setUser(null)
    setTokens(null)
  }

  const setAuth = (newTokens: AuthTokens, newUser: WebbaUser) => {
    WebbaID.saveTokens(newTokens)
    WebbaID.saveUser(newUser)
    setTokens(newTokens)
    setUser(newUser)
  }

  return (
    <AuthContext.Provider value={{ user, tokens, isLoading, login, logout, setAuth }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}`}
                      language="typescript"
                    />

                    <Divider />

                    <Text weight="medium">Utilisation :</Text>
                    <CodeBlock
                      code={`// Dans App.tsx
import { AuthProvider } from '@/contexts/AuthContext'

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>...</Routes>
      </Router>
    </AuthProvider>
  )
}

// Dans un composant
import { useAuth } from '@/contexts/AuthContext'

function Header() {
  const { user, login, logout, isLoading } = useAuth()

  if (isLoading) return <div>Chargement...</div>

  return user ? (
    <div>
      <span>Bonjour {user.name}</span>
      <button onClick={logout}>Deconnexion</button>
    </div>
  ) : (
    <button onClick={login}>Connexion</button>
  )
}`}
                      language="typescript"
                    />
                  </VStack>
                </Card>
              </Animate>
            </div>
          </VStack>
        </div>

        {isDesktop && (
          <div style={{
            position: 'sticky',
            top: '2rem',
            width: '220px',
            flexShrink: 0
          }}>
            <TableOfContents
              items={tocItems}
              title="Sur cette page"
              autoTrack
              scrollOffset={100}
              variant="bordered"
            />
          </div>
        )}
      </HStack>
    </div>
  )
}
